/** @jsxImportSource @emotion/react */
/**
 * Карточка посадки для галереи
 */
import React from 'react';
import { Card, Space } from 'antd';
import { EyeOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import type { Planting } from '../../../models/Planting';
import PlantingLifecycleTag from '../../../features/plantings/components/PlantingLifecycleTag';
import PlantingBbchTag from './PlantingBbchTag';
import { formatDateTime, formatPeriodSummary, getFullPhotoUrl, getLocationCode, getPlantingDisplayQuantity } from '../utils/plantingsFormatters';
import { Thumbnail, ImagePlaceholder, PlantingMetaText, PlantingSecondaryText, HighlightedText } from '../../../styles/components';

interface PlantingCardProps {
  planting: Planting;
  onView: (planting: Planting) => void;
  onEdit?: (planting: Planting) => void;
  onDelete?: (planting: Planting) => void;
  showLocationCode?: boolean; // Показывать ли код локации (в режиме без группировки)
}

const PlantingCard: React.FC<PlantingCardProps> = ({
  planting,
  onView,
  onEdit,
  onDelete,
  showLocationCode = false,
}) => {
  const photoUrl = planting.photos && planting.photos.length > 0
    ? getFullPhotoUrl(planting.photos[0].thumbnailUrl || planting.photos[0].url)
    : null;
  const displayQuantity = getPlantingDisplayQuantity(planting);

  const actions = [];
  actions.push(<EyeOutlined key="view" onClick={(e) => { e.stopPropagation(); onView(planting); }} />);
  if (onEdit) {
    actions.push(<EditOutlined key="edit" onClick={(e) => { e.stopPropagation(); onEdit(planting); }} />);
  }
  if (onDelete) {
    actions.push(<DeleteOutlined key="delete" onClick={(e) => { e.stopPropagation(); onDelete(planting); }} />);
  }

  return (
    <Card
      hoverable
      onClick={() => onView(planting)}
      cover={
        photoUrl ? (
          <Thumbnail
            alt={planting.crop?.name}
            src={photoUrl}
            size="large"
          />
        ) : (
          <ImagePlaceholder size="large">
            <span>Нет фото</span>
          </ImagePlaceholder>
        )
      }
      actions={actions.length > 0 ? actions : undefined}
    >
      <Card.Meta
        title={
          <Space direction="vertical" size={4}>
            <Space>
              <div>{planting.crop?.name || 'Без названия'}</div>
              {displayQuantity >= 1 && (
                <HighlightedText>
                  × {displayQuantity}
                </HighlightedText>
              )}
            </Space>
            <Space wrap size={[4, 4]}>
              <PlantingLifecycleTag status={planting.lifecycleStatus} />
              <PlantingBbchTag bbch={planting.bbch} />
            </Space>
          </Space>
        }
        description={
          <Space direction="vertical" size={2}>
            {showLocationCode && (
              <PlantingMetaText>Локация: {getLocationCode(planting)}</PlantingMetaText>
            )}
            <PlantingMetaText>Создана: {formatDateTime(planting.createdAt)}</PlantingMetaText>
            <PlantingSecondaryText>Период: {formatPeriodSummary(planting)}</PlantingSecondaryText>
          </Space>
        }
      />
    </Card>
  );
};

export default PlantingCard;
